import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import {
  SupplierCategoriesMultiSelect,
  type SupplierCategoryOption,
} from './SupplierCategoriesMultiSelect';
import { SupplierCategoryBadges } from './SupplierCategoryBadges';

export type SupplierFormValues = {
  name: string;
  contact: string;
  email: string;
  phone: string;
  selectedCategoryIds: string[];
  desvio_comercial_pct: string;
  beneficio_comercial_pct: string;
  notes: string;
  active: boolean;
};

type Mode = 'create' | 'edit';

interface SupplierDialogProps {
  open: boolean;
  mode?: Mode;
  initialValues?: Partial<SupplierFormValues>;
  categoryOptions?: SupplierCategoryOption[];
  categoryLoading?: boolean;
  isSubmitting?: boolean;
  onSubmit: (values: SupplierFormValues) => Promise<void> | void;
  onClose: () => void;
}

const EMPTY_VALUES: SupplierFormValues = {
  name: '',
  contact: '',
  email: '',
  phone: '',
  selectedCategoryIds: [],
  desvio_comercial_pct: '',
  beneficio_comercial_pct: '',
  notes: '',
  active: true,
};

export function SupplierDialog({
  open,
  mode = 'create',
  initialValues,
  categoryOptions = [],
  categoryLoading = false,
  isSubmitting = false,
  onSubmit,
  onClose,
}: SupplierDialogProps) {
  const computedInitialValues = useMemo(
    () => ({
      ...EMPTY_VALUES,
      ...initialValues,
      desvio_comercial_pct:
        initialValues?.desvio_comercial_pct != null
          ? String(initialValues.desvio_comercial_pct)
          : '',
      beneficio_comercial_pct:
        initialValues?.beneficio_comercial_pct != null
          ? String(initialValues.beneficio_comercial_pct)
          : '',
      notes: initialValues?.notes ?? '',
      active: initialValues?.active ?? true,
    }),
    [initialValues],
  );

  const form = useForm<SupplierFormValues>({ defaultValues: computedInitialValues });

  useEffect(() => { if (open) form.reset(computedInitialValues); }, [open, computedInitialValues, form]);

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit({
      ...values,
      desvio_comercial_pct: values.desvio_comercial_pct?.trim() || '0',
      beneficio_comercial_pct: values.beneficio_comercial_pct?.trim() || '0',
      notes: values.notes?.trim() || '',
      active: Boolean(values.active),
    });
  });

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !isSubmitting) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Editar proveedor' : 'Nuevo proveedor'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                rules={{ required: 'Nombre obligatorio' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre</FormLabel>
                    <FormControl><Input disabled={isSubmitting} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="contact"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contacto</FormLabel>
                    <FormControl><Input disabled={isSubmitting} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="email"
                rules={{ required: 'Email obligatorio' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl><Input type="email" disabled={isSubmitting} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Teléfono</FormLabel>
                    <FormControl><Input disabled={isSubmitting} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="selectedCategoryIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categorías</FormLabel>
                  <FormControl>
                    <SupplierCategoriesMultiSelect
                      options={categoryOptions}
                      selectedValues={field.value ?? []}
                      placeholder="Selecciona categorías"
                      emptyStateLabel="No hay categorías disponibles."
                      disabled={isSubmitting || categoryLoading}
                      ariaLabel="Seleccionar categorías del proveedor"
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <div className="pt-2">
                    <SupplierCategoryBadges
                      categories={categoryOptions
                        .filter((category) => (field.value ?? []).includes(category.value))
                        .map((category) => category.label)}
                    />
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="desvio_comercial_pct"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Desvío comercial (%)</FormLabel>
                    <FormControl><Input type="number" step="0.01" inputMode="decimal" disabled={isSubmitting} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="beneficio_comercial_pct"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Beneficio comercial (%)</FormLabel>
                    <FormControl><Input type="number" step="0.01" inputMode="decimal" disabled={isSubmitting} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <FormLabel>Activo</FormLabel>
                      <p className="text-sm text-gray-500">Aparece en catálogos y pedidos</p>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isSubmitting} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl><Textarea rows={3} disabled={isSubmitting} {...field} /></FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : mode === 'edit' ? 'Guardar cambios' : 'Crear proveedor'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
